import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Grid } from '@mui/material';
import moment from 'moment';

import { FHAntdDateTimePicker, FHAutocomplete, FHTextarea } from '../../../../../../../base';
import { useTreatmentPlanEditable } from '../../../../../hooks/useTreatmentPlanEditable';
import { assessmentMethodsSchema } from '../../../../utils/validationSchema';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../../../../../constants';
import { TOOL_OPTIONS, TOOL_DESCRIPTIONS } from '../../../../constants/general';
import { ERROR_STATES, PERSIST_DATA_STATES, STATE_IDS } from '../../../../constants/states';
import { assessmentActions } from '../../../../../../../../store';
import {
  debounce,
  validateSync,
  getAllInvalidFieldNames,
  disabledFutureDate,
} from '../../../../../../../../helpers';
import { TreatmentPlanResourceVersion } from '../../../../../constants/general';

import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IAssessmentTool } from '../../../../../../../../utils/types';
import type { ToolId } from '../../../../constants/types/general';

interface AssessmentMethodsProps {
  assessmentTool?: IAssessmentTool;
}

function AssessmentMethods({ assessmentTool }: AssessmentMethodsProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const editable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { assessment, persistDataState } = useSelector((state: any) => state.assessments);
  const assessmentVersion = treatmentPlan?.resource_versions.assessment;

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${
            assessmentTool?.id ? assessmentTool.id : 'new'
          }`]: errorCount,
        },
      }),
    );
  };
  const updatePersistedData = (assessmentToolData: IAssessmentTool) => {
    const isValid = validateSync(assessmentMethodsSchema, {
      ...assessmentToolData,
      assessment_version: assessmentVersion,
    });
    const data = isValid ? null : assessmentToolData;
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${
            assessmentTool?.id ? assessmentTool.id : 'new'
          }`]: data,
        },
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);
  const debouncedDataPersister = useCallback(debounce(updatePersistedData, 500), []);

  const [assessmentMethodInfo, setAssessmentMethodInfo] = useState<IAssessmentTool>({
    tool_name: '',
    date_tool_used: null,
    reason_for_use_age_group: '',
    reason_for_use_symptoms: '',
    score: '',
    description: '',
  });

  const [errors, setErrors] = useState<any>({});

  const { id: planId } = treatmentPlan;

  useEffect(() => {
    debouncedErrorUpdater(errors);
    // eslint-disable
  }, [errors]);

  useEffect(() => {
    const persistedData =
      persistDataState[
        `${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${
          assessmentTool?.id ? assessmentTool.id : 'new'
        }`
      ];
    if (!persistedData) {
      if (!assessmentTool) {
        setErrors({});
      }
      return;
    }

    setAssessmentMethodInfo(persistedData);
    const data = prepareDataForValidation(persistedData);
    const errorsData = getAllInvalidFieldNames(assessmentMethodsSchema, {
      ...data,
      assessment_version: assessmentVersion,
    });
    setErrors(errorsData);
    // eslint-disable
  }, [planId, persistDataState, assessmentVersion]);

  useEffect(() => {
    if (!assessmentTool?.id) return;

    const persistedData =
      persistDataState[
        `${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${
          assessmentTool?.id ? assessmentTool.id : 'new'
        }`
      ];
    if (persistedData) return;

    setAssessmentMethodInfo(assessmentTool);
    const data = prepareDataForValidation(assessmentTool);
    const errorsData = getAllInvalidFieldNames(assessmentMethodsSchema, {
      ...data,
      assessment_version: assessmentVersion,
    });
    setErrors(errorsData);
  }, [assessmentTool, assessmentVersion]);

  const handleFieldUpdate = (field: string, value: string, field2?: string, value2?: string) => {
    let updatedData = { ...assessmentMethodInfo, [field]: value };
    handleValidate(updatedData);
    if (field2 && value2) {
      updatedData = { ...updatedData, [field2]: value2 };
      handleValidate(updatedData);
    }
    setAssessmentMethodInfo(updatedData);
    debouncedDataPersister(updatedData);
  };

  const handleValidate = (data: IAssessmentTool = assessmentMethodInfo) => {
    if (!data) return;
    const schemaData = prepareDataForValidation(data);
    const invalidFields = getAllInvalidFieldNames(assessmentMethodsSchema, {
      ...schemaData,
      assessment_version: assessmentVersion,
    });
    setErrors(invalidFields);
  };

  const prepareDataForValidation = (data: IAssessmentTool) => {
    const dataForValidation = { ...data };
    delete dataForValidation.id;
    delete dataForValidation.visuals;
    return dataForValidation;
  };

  const handleCreateAndUpdateAssessmentTool = () => {
    const isValid = validateSync(assessmentMethodsSchema, {
      ...assessmentMethodInfo,
      assessment_version: assessmentVersion,
    });
    if (!isValid || !planId || !assessment?.id) return;

    if (!assessmentTool?.id) {
      // Create assessment tool
      dispatch(
        assessmentActions.createTool({
          data: assessmentMethodInfo,
          getAccessTokenSilently,
          planId,
          assessmentId: assessment.id,
        }),
      );
    } else {
      // Update assessment tool
      dispatch(
        assessmentActions.updateTool({
          data: assessmentMethodInfo,
          getAccessTokenSilently,
          planId,
          assessmentId: assessment.id,
          toolId: assessmentTool.id,
        }),
      );
    }
  };

  return (
    <Box>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={assessmentVersion === TreatmentPlanResourceVersion.V2 ? 6 : 12}>
          <Typography fontWeight="bold" mb={0.5} textAlign="left">
            Tool used*
          </Typography>
          <FHAutocomplete
            data-testid="toolsUsedDropDown"
            sx={{ flexGrow: 1, minWidth: 240 }}
            value={
              TOOL_OPTIONS?.find((option) => option.id === assessmentMethodInfo?.tool_name) || null
            }
            options={TOOL_OPTIONS}
            onChange={(e: ChangeEvent<HTMLSelectElement>, newValue: { [key: string]: string }) => {
              if (!newValue?.id) return;
              handleFieldUpdate(
                'tool_name',
                newValue.id,
                'description',
                TOOL_DESCRIPTIONS[newValue.id as ToolId],
              );
            }}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            disabled={!editable}
            inputHeight={40}
            colorTheme={Themes.Primary}
            disableClearable
            onBlur={handleCreateAndUpdateAssessmentTool}
            error={errors['tool_name']}
          />
        </Grid>
        {assessmentVersion === TreatmentPlanResourceVersion.V2 && (
          <Grid item xs={12} md={6}>
            <Typography fontWeight="bold" mb={0.5} textAlign="left">
              Assessment date*
            </Typography>
            <FHAntdDateTimePicker
              data-testid="assessmentDatePicker"
              format="MM/DD/YY"
              allowClear={false}
              value={
                assessmentMethodInfo.date_tool_used
                  ? moment(assessmentMethodInfo.date_tool_used)
                  : null
              }
              style={{ width: '100%' }}
              colorTheme={Themes.Primary}
              inputHeight={40}
              disabled={!editable}
              disabledDate={disabledFutureDate}
              onChange={(e: Moment) => handleFieldUpdate('date_tool_used', e.format('YYYY-MM-DD'))}
              onBlur={() => handleCreateAndUpdateAssessmentTool()}
              error={errors['date_tool_used']}
            />
          </Grid>
        )}
      </Grid>
      <Typography fontWeight="bold" mb={0.5} textAlign="left">
        Rationale for assessment selection - age group of patient vs. assessment:*
      </Typography>
      <FHTextarea
        data-testid="reasonForUseAgeGroupTextField"
        fullWidth
        minRows={3}
        maxRows={6}
        colorTheme={Themes.Primary}
        disabled={!editable}
        expandable={editable}
        minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
        value={assessmentMethodInfo.reason_for_use_age_group}
        onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
          handleFieldUpdate('reason_for_use_age_group', e.target.value)
        }
        onBlur={handleCreateAndUpdateAssessmentTool}
        onCloseExpandDialog={handleCreateAndUpdateAssessmentTool}
        error={errors['reason_for_use_age_group']}
        helperText={
          errors['reason_for_use_age_group']
            ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
            : ''
        }
      />
      <Box mt={2}>
        <Typography fontWeight="bold" mb={0.5} textAlign="left">
          Rationale for assessment selection - Patient symptoms noted that supported assessment
          selection:*
        </Typography>
        <FHTextarea
          data-testid="reasonForUseSymptomsTextField"
          fullWidth
          minRows={3}
          maxRows={6}
          colorTheme={Themes.Primary}
          disabled={!editable}
          expandable={editable}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          value={assessmentMethodInfo.reason_for_use_symptoms}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldUpdate('reason_for_use_symptoms', e.target.value)
          }
          onBlur={handleCreateAndUpdateAssessmentTool}
          onCloseExpandDialog={handleCreateAndUpdateAssessmentTool}
          error={errors['reason_for_use_symptoms']}
          helperText={
            errors['reason_for_use_symptoms']
              ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
              : ''
          }
        />
      </Box>
      <Box mt={2}>
        <Typography fontWeight="bold" mb={0.5} textAlign="left">
          Tool description and modules used:
        </Typography>
        <FHTextarea
          data-testid="descriptionToolsModulesUsedTextField"
          fullWidth
          minRows={3}
          maxRows={10}
          colorTheme={Themes.Primary}
          disabled={!editable}
          expandable={editable}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          value={assessmentMethodInfo.description}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldUpdate('description', e.target.value)
          }
          onBlur={handleCreateAndUpdateAssessmentTool}
          onCloseExpandDialog={handleCreateAndUpdateAssessmentTool}
          error={errors['description']}
          helperText={
            errors['description'] ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters` : ''
          }
        />
      </Box>
      <Box mt={2}>
        <Typography fontWeight="bold" mb={0.5} textAlign="left">
          Describe the client score (have all scoring presented correctly based on the way the
          assessment is scored):*
        </Typography>
        <FHTextarea
          data-testid="assesmentScoreTextField"
          fullWidth
          minRows={3}
          maxRows={6}
          colorTheme={Themes.Primary}
          disabled={!editable}
          expandable={editable}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          value={assessmentMethodInfo.score}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldUpdate('score', e.target.value)
          }
          onBlur={handleCreateAndUpdateAssessmentTool}
          onCloseExpandDialog={handleCreateAndUpdateAssessmentTool}
          error={errors['score']}
          helperText={
            errors['score'] ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters` : ''
          }
        />
      </Box>
    </Box>
  );
}

export default AssessmentMethods;
